import { type FC, type JSX, useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { RootState } from 'src/redux/rootReducerType';
import {
  setProfileHouseholdCompositionConfirmed,
  setUserHouseholds,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceSlice';
import type { UHMember } from 'src/types/Certification';
import axios from 'src/utils/axios';
import { userService } from 'src/services/User.service';
import { InfoBox } from 'src/components/RenterSettings/help/HelpInfoBox';
import useHelpInfoBox from 'src/components/RenterSettings/help/useHelpInfoBox';
import { HELP_BOX_DISMISS_KEYS, HOUSEHOLD_STRUCTURE_STEPS } from '../constants';
import HouseholdInfoBoxContent from '../Household/HouseholdInfoBoxContent';
import HouseholdConfirmSuccessView from './HouseholdConfirmSuccessView';
import AddUserHouseholdMemberScreen from '../AccountHub/AddUserHouseholdMemberScreen';
import ProfileHouseholdMembersList from './ProfileHouseholdMembersList';
import EditUserHouseholdMemberScreen from './EditUserHouseholdMemberScreen';
import {
  getProfileMemberFullName,
  resolveProfileHousehold,
} from './profileHouseholdUtils';

/**
 * Forced (non-dismissible) composition prompt for cert-less / profile-only
 * renters, who have a UserHousehold but no certification.
 *
 * The profile household is resolved explicitly via resolveProfileHousehold —
 * the first *unconfirmed* managing-role household that is not already backed by
 * a certification — never `userHouseholds[0]`. The cert HouseholdCompositionModal
 * owns cert-linked households; this modal stays out of its way.
 *
 * Open state is derived (there is no Redux open flag): the modal is visible
 * whenever an unconfirmed profile household exists on the renter home and the
 * cert composition modal is not already open. Confirming removes the household
 * from the unconfirmed list, and a local `success` flag keeps the success view
 * mounted until the renter closes it.
 */
const ProfileHouseholdCompositionModal: FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const helpBox = useHelpInfoBox(HELP_BOX_DISMISS_KEYS.HOUSEHOLD_COMPOSITION);

  const unconfirmedProfileHouseholds = useSelector(
    (state: RootState) =>
      state.newRenterInterface.unconfirmedProfileHouseholds ?? []
  );
  const certifications = useSelector(
    (state: RootState) => state.newRenterInterface.certifications ?? []
  );
  const openHouseholdComposition = useSelector(
    (state: RootState) => state.newRenterInterface.openHouseholdComposition
  );
  const openHouseholdCompositionForCertId = useSelector(
    (state: RootState) =>
      state.newRenterInterface.openHouseholdCompositionForCertId
  );
  const userId = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user?.id
  );

  const [step, setStep] = useState<HOUSEHOLD_STRUCTURE_STEPS>(
    HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY
  );
  const [memberToEditId, setMemberToEditId] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<UHMember | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const profileHousehold = useMemo(
    () => resolveProfileHousehold(unconfirmedProfileHouseholds, certifications),
    [unconfirmedProfileHouseholds, certifications]
  );

  const isRenterHome =
    location.pathname === '/renter' || location.pathname === '/renter/';
  const certModalOpen =
    openHouseholdComposition || openHouseholdCompositionForCertId !== null;

  const isModalOpen =
    (success || profileHousehold !== null) && isRenterHome && !certModalOpen;

  const memberToEdit = useMemo<UHMember | null>(
    () =>
      profileHousehold?.members?.find(
        (m) => m.user_household_member_id === memberToEditId
      ) ?? null,
    [profileHousehold, memberToEditId]
  );

  const refreshHouseholds = useCallback(async (): Promise<void> => {
    try {
      const { data } = await userService.fetchMyHousehold();
      dispatch(setUserHouseholds(data ?? []));
    } catch {
      // Non-critical — the existing list stays visible; a reload re-syncs.
    }
  }, [dispatch]);

  const handleConfirm = async (): Promise<void> => {
    if (!profileHousehold) {
      return;
    }
    setConfirmError(null);
    setConfirming(true);
    try {
      await axios.post(
        `v2/renter-hub/me/my-household/${profileHousehold.user_household_id}/confirm_composition/`
      );
      setSuccess(true);
      dispatch(
        setProfileHouseholdCompositionConfirmed({
          userHouseholdId: profileHousehold.user_household_id,
        })
      );
    } catch {
      setConfirmError('Unable to confirm. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  const handleConfirmRemove = async (): Promise<void> => {
    if (!memberToRemove) {
      return;
    }
    setRemoveError(null);
    setRemoving(true);
    try {
      await axios.delete(
        `v2/renter-hub/me/my-household/members/${memberToRemove.user_household_member_id}/`
      );
      setMemberToRemove(null);
      await refreshHouseholds();
    } catch {
      setRemoveError('Unable to remove member. Please try again.');
    } finally {
      setRemoving(false);
    }
  };

  const handleMemberAdded = useCallback(async (): Promise<void> => {
    setStep(HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY);
    await refreshHouseholds();
  }, [refreshHouseholds]);

  const handleMemberSaved = useCallback(async (): Promise<void> => {
    setMemberToEditId(null);
    setStep(HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY);
    await refreshHouseholds();
  }, [refreshHouseholds]);

  const handleAddMember = (): void =>
    setStep(HOUSEHOLD_STRUCTURE_STEPS.ADD_MEMBER);

  const handleEditMember = (member: UHMember): void => {
    setMemberToEditId(member.user_household_member_id);
    setStep(HOUSEHOLD_STRUCTURE_STEPS.EDIT_MEMBER);
  };

  const handleBackToList = (): void => {
    setMemberToEditId(null);
    setStep(HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY);
  };

  if (!isModalOpen) {
    return null;
  }

  const title =
    step === HOUSEHOLD_STRUCTURE_STEPS.ADD_MEMBER
      ? 'New Household Member'
      : step === HOUSEHOLD_STRUCTURE_STEPS.EDIT_MEMBER
      ? 'Edit Household Member'
      : 'Household Composition';

  const renderBody = (): JSX.Element | null => {
    if (success) {
      return <HouseholdConfirmSuccessView onNext={() => setSuccess(false)} />;
    }
    if (!profileHousehold) {
      return null;
    }
    switch (step) {
      case HOUSEHOLD_STRUCTURE_STEPS.ADD_MEMBER:
        return (
          <AddUserHouseholdMemberScreen
            userHouseholdId={profileHousehold.user_household_id}
            onSubmit={() => void handleMemberAdded()}
            onCancel={handleBackToList}
          />
        );
      case HOUSEHOLD_STRUCTURE_STEPS.EDIT_MEMBER:
        if (!memberToEdit) {
          return null;
        }
        return (
          <EditUserHouseholdMemberScreen
            member={memberToEdit}
            onSaved={() => void handleMemberSaved()}
            onCancel={handleBackToList}
          />
        );
      case HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY:
      default:
        return (
          <ProfileHouseholdMembersList
            household={profileHousehold}
            currentUserId={userId}
            canManage
            onAddMember={handleAddMember}
            onEditMember={handleEditMember}
            onRemoveMember={setMemberToRemove}
          />
        );
    }
  };

  const showConfirm =
    !success &&
    step === HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY &&
    profileHousehold !== null;

  return (
    <>
      <Dialog
        open={isModalOpen}
        disableEscapeKeyDown
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        slotProps={{
          paper: {
            sx: {
              margin: isMobile ? '0 !important' : 4,
              height: isMobile ? '100%' : '80dvh',
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? theme.palette.background.default
                  : theme.palette.background.neutral,
            },
          },
        }}
      >
        {/* ── Green header ── */}
        <Box
          sx={{
            backgroundColor: 'primary.dark',
            px: 3,
            pt: 2.5,
            pb: 2,
            flexShrink: 0,
          }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            {step !== HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY && (
              <IconButton
                size="small"
                onClick={handleBackToList}
                aria-label="Back to household list"
                sx={{
                  backgroundColor: 'common.white',
                  color: 'grey.900',
                  p: 0.75,
                  flexShrink: 0,
                  '&:hover': { backgroundColor: 'grey.200' },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}

            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ color: 'primary.contrastText', flex: 1 }}
            >
              {title}
            </Typography>

            <Tooltip
              title="Click to see information about household composition"
              placement="bottom"
              arrow
              slotProps={{ tooltip: { sx: { maxWidth: 400, p: 2 } } }}
            >
              <IconButton onClick={helpBox.handleToggle} size="small">
                <HelpOutlineIcon
                  sx={{
                    color: 'primary.contrastText',
                    fontSize: 20,
                    opacity: 0.8,
                  }}
                />
              </IconButton>
            </Tooltip>
          </Stack>

          {profileHousehold?.name && !success && (
            <Typography
              variant="body2"
              sx={{
                color: 'primary.contrastText',
                opacity: 0.75,
                mt: 0.5,
                ml:
                  step !== HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY
                    ? 4.5
                    : 0,
              }}
            >
              {profileHousehold.name}
            </Typography>
          )}
        </Box>

        <DialogContent>
          {step === HOUSEHOLD_STRUCTURE_STEPS.HOUSEHOLD_DISPLAY && !success && (
            <>
              <InfoBox
                onDismiss={helpBox.handleDismiss}
                shouldShow={helpBox.showInfo}
              >
                <HouseholdInfoBoxContent canManage />
              </InfoBox>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, mb: 2 }}
              >
                Household modifications will be reviewed before confirming
                eligibility.
              </Typography>
            </>
          )}

          {renderBody()}

          {confirmError && (
            <DialogContentText color="error" sx={{ mt: 2 }}>
              {confirmError}
            </DialogContentText>
          )}
        </DialogContent>

        {showConfirm && (
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => void handleConfirm()}
              color="primary"
              variant="contained"
              size="large"
              disabled={confirming}
              fullWidth={isMobile}
              startIcon={
                confirming ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
            >
              <span>{confirming ? 'Confirming…' : 'Confirm Household'}</span>
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <Dialog
        open={memberToRemove !== null}
        onClose={() => {
          if (!removing) {
            setMemberToRemove(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove household member?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {memberToRemove ? getProfileMemberFullName(memberToRemove) : ''}{' '}
            will be removed from your household. You can add them again later.
          </DialogContentText>
          {removeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {removeError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemberToRemove(null)} disabled={removing}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disableElevation
            onClick={() => void handleConfirmRemove()}
            disabled={removing}
            startIcon={
              removing ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            <span>{removing ? 'Removing…' : 'Remove'}</span>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProfileHouseholdCompositionModal;
